'use client'
import { useEffect, useRef } from 'react'

export type MapPin = {
  lat: number
  lng: number
  label: string
  type: 'place' | 'service' | 'institution' | 'cluster' | 'event'
  url?: string
}

const TYPE_COLOR: Record<string, string> = {
  place:       '#B5512F',
  service:     '#5B6B73',
  institution: '#6E4E6E',
  cluster:     '#4C6B50',
  event:       '#B07A2B',
}

const GORIS_LAT = 39.5127
const GORIS_LNG = 46.3543

// Fallback cluster centers — used when cluster.location is not set in CMS
const CLUSTER_CENTERS: Record<string, [number, number]> = {
  'old-goris':         [39.5108, 46.3318],
  'goris-city-center': [39.5145, 46.3455],
  'verishen':          [39.4462, 46.3038],
  'old-khndzoresk':    [39.4908, 46.4135],
  'old-khot':          [39.5545, 46.4318],
  'old-shinuhayr':     [39.3888, 46.3352],
  'tatev':             [39.3812, 46.2482],
}

export function ClusterMap({ pins = [], centerLat, centerLng, slug }: {
  pins?: MapPin[]
  centerLat?: number | null
  centerLng?: number | null
  slug?: string
}) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<any>(null)

  useEffect(() => {
    const el = mapRef.current
    if (!el || mapInstance.current || el.classList.contains('leaflet-container')) return

    import('leaflet').then(L => {
      if (!mapRef.current || mapRef.current.classList.contains('leaflet-container')) return

      delete (L.Icon.Default.prototype as any)._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      })

      // Resolve cluster center: explicit CMS GPS > slug fallback > Goris default
      const fallback = slug ? CLUSTER_CENTERS[slug] : null
      const centerLa = centerLat ?? fallback?.[0] ?? GORIS_LAT
      const centerLo = centerLng ?? fallback?.[1] ?? GORIS_LNG

      const map = L.map(mapRef.current!, { zoomControl: true, scrollWheelZoom: false })
      mapInstance.current = map

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map)

      // Always start centered on the cluster's geographic area
      map.setView([centerLa, centerLo], 13)

      if (pins.length > 0) {
        pins.forEach(pin => {
          const color = TYPE_COLOR[pin.type] || '#4C6B50'
          const icon = L.divIcon({
            className: '',
            html: `<div style="width:24px;height:24px;border-radius:50% 50% 50% 0;rotate:-45deg;background:${color};border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 24],
            popupAnchor: [0, -28],
          })
          const marker = L.marker([pin.lat, pin.lng], { icon })
          marker.bindPopup(pin.url
            ? `<a href="${pin.url}" style="font-weight:600;color:#1E1A16;text-decoration:none">${pin.label}</a>`
            : `<span style="font-weight:600">${pin.label}</span>`)
          marker.addTo(map)
        })
      } else {
        L.marker([centerLa, centerLo]).addTo(map).bindPopup('Goris, Armenia').openPopup()
      }
    })

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove()
        mapInstance.current = null
      }
    }
  }, [])

  return (
    <>
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </>
  )
}
